import { useLang } from "../context/LanguageContext";
import { IMAGES } from "../data/content";
import { Reveal } from "./Reveal";
import { Check, ArrowRight } from "lucide-react";

const Tag = ({ children, light }) => (
  <span className={`inline-block text-xs font-semibold uppercase tracking-widest mb-5 ${light ? "text-accent" : "text-primary"}`}>{children}</span>
);

export const Sportello = () => {
  const { t } = useLang();
  const s = t.sportello;
  return (
    <section id="sportello" className="py-24 md:py-32 relative z-10">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        <div className="lg:col-span-5 order-2 lg:order-1">
          <Reveal delay={0.1}>
            <div className="rounded-3xl overflow-hidden aspect-[4/5] border border-black/5 group">
              <img src={IMAGES.classroom} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7 order-1 lg:order-2">
          <Reveal><Tag>{s.tag}</Tag></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display font-extrabold tracking-tight text-4xl md:text-6xl leading-[1.02] mb-6">{s.title}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-xl mb-8">{s.body}</p>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
            {s.services.map((item, i) => (
              <Reveal key={i} delay={0.08 * (i % 2)}>
                <div className="flex items-start gap-3 border-b border-foreground/10 pb-4">
                  <span className="mt-0.5 grid place-items-center w-6 h-6 rounded-full bg-accent text-accent-foreground shrink-0">
                    <Check size={14} strokeWidth={3} />
                  </span>
                  <span className="text-sm md:text-base font-medium">{item}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export const Projects = () => {
  const { t } = useLang();
  const p = t.projects;
  return (
    <section id="projects" className="py-24 md:py-32 bg-foreground text-background relative z-10">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="max-w-2xl mb-16">
          <Reveal><Tag light>{p.tag}</Tag></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display font-extrabold tracking-tight text-4xl md:text-6xl leading-[1.02]">{p.title}</h2>
          </Reveal>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {p.list.map((proj, i) => (
            <Reveal key={i} delay={(i % 3) * 0.08}>
              <div className="group h-full rounded-2xl border border-white/10 p-7 hover:bg-white/5 transition-colors">
                <div className="flex items-center justify-between mb-8">
                  <span className="font-display text-sm text-background/40">{String(i + 1).padStart(2, "0")}</span>
                  <ArrowRight size={18} className="text-accent -translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all" />
                </div>
                <h3 className="font-display font-bold text-xl md:text-2xl tracking-tight mb-2">{proj.t}</h3>
                <p className="text-sm text-background/60 leading-relaxed">{proj.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Events = () => {
  const { t } = useLang();
  const e = t.events;
  return (
    <section id="events" className="py-24 md:py-32 relative z-10">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-4">
          <Reveal><Tag>{e.tag}</Tag></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display font-extrabold tracking-tight text-4xl md:text-5xl leading-[1.02] mb-8">{e.title}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl overflow-hidden aspect-[4/3] border border-black/5 group">
              <img src={IMAGES.community} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-8">
          {e.list.map((ev, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div className="group grid grid-cols-[auto_1fr] gap-6 items-baseline py-8 border-t border-foreground/15 hover:pl-2 transition-all">
                <span className="font-display font-light text-2xl md:text-4xl text-primary/30">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-2 group-hover:text-primary transition-colors">{ev.t}</h3>
                  <p className="text-sm md:text-base text-muted-foreground leading-relaxed max-w-xl">{ev.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Roadmap = () => {
  const { t } = useLang();
  const r = t.roadmap;
  return (
    <section id="roadmap" className="py-24 md:py-32 bg-secondary relative z-10">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="max-w-2xl mb-16">
          <Reveal><Tag>{r.tag}</Tag></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display font-extrabold tracking-tight text-4xl md:text-6xl leading-[1.02]">{r.title}</h2>
          </Reveal>
        </div>
        <div className="grid md:grid-cols-5 gap-6">
          {r.steps.map((step, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="relative pt-6 border-t-2 border-primary h-full">
                <div className="absolute -top-[7px] left-0 w-3 h-3 rounded-full bg-primary" />
                <div className="font-display font-extrabold text-3xl tracking-tight mb-3">{step.y}</div>
                <h3 className="font-semibold text-lg mb-2">{step.t}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
